import React, { useEffect, useState } from 'react'
import { Box, Paper, Theme, Typography, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineDotComponent from '../../../screens/App/Events/TimelineDotComponent'
import TimelineConnector from '@mui/lab/TimelineConnector'
import { makeStyles } from '@mui/styles'
import TimelineContent from '@mui/lab/TimelineContent'
import { timelineItemClasses } from '@mui/lab'
import getDataBiography from '../../../screens/App/Dashboard/getDataBiography'
import { useDispatch, useSelector } from 'react-redux'
import { getBiography } from '../../../store/settings/settings.selectors'
import DOMPurify from 'dompurify'
import { getAppTheme } from '../../../shared/config/getAppTheme'
import UndoIcon from '@mui/icons-material/Undo'
import * as settingsActions from '../../../store/settings/settings.actions'
import { useHistory } from 'react-router-dom'

const useStyles = makeStyles(() => ({
  timeline: {
    flexGrow: '0!important',
    width: '100%',
    height: 'auto',
    overflowX: 'hidden',
  },
}))

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <Box
    sx={{
      margin: 0,
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'flex-end',
    }}
    onClick={onClick}
  >
    <UndoIcon />
  </Box>
)

const Biography: React.FC = () => {
  const bookmark = window.localStorage.getItem('bookmarkOttoMeissner')
  const history = useHistory()
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()
  const theme: Theme = getAppTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const biography = useSelector(getBiography)
  const data = biography ? getDataBiography(biography) : undefined

  const [yearIndex, setYearIndex] = useState<number | undefined>()

  const handleScroll = () => {
    window.localStorage.setItem('bookmarkOttoMeissner', window.scrollY.toString())
  }

  useEffect(() => {
    if (bookmark) {
      window.scrollTo({ top: parseInt(bookmark, 10), behavior: 'smooth' })
    }
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [bookmark])

  const handleBackToHome = () => {
    window.removeEventListener('scroll', handleScroll)
    history.replace('/')
    dispatch(settingsActions.setNavPoint([]))
  }

  const renderContent = (index: number) => {
    const element = data?.[index]
    if (!element) return null

    return (
      <>
        {element.title && (
          <Typography variant='subtitle1' sx={{ marginTop: 0 }}>
            {element.title}
          </Typography>
        )}
        {element.description && (
          <Typography
            variant='body1'
            sx={{ marginTop: 0, fontStyle: 'italic' }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(element.description),
            }}
          />
        )}
        {element.image && (
          <img
            src={element.image}
            alt={element.title || 'Biography image'}
            style={{ width: '100%', height: 'auto' }}
          />
        )}
        {!downSm && <BackButton onClick={handleBackToHome} />}
      </>
    )
  }

  const renderTimelineView = () => (
    <>
      <Box>
        <Typography variant='h1'>{t(`labels:${biography}`)}</Typography>
      </Box>
      <Timeline
        position='right'
        className={classes.timeline}
        sx={{
          padding: 0,
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
      >
        {data?.map((element, index) => (
          <TimelineItem key={index}>
            <TimelineSeparator>
              <TimelineDot
                onClick={() => downSm && setYearIndex(index)}
                color='primary'
                variant='outlined'
              >
                <TimelineDotComponent
                  year={element.date.year}
                  month={element.date.month}
                  day={element.date.day}
                  active={false}
                />
              </TimelineDot>
              {index < (data.length - 1) && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              {downSm ? (
                <Box
                  sx={{ minHeight: '70px', cursor: 'pointer' }}
                  onClick={() => setYearIndex(index)}
                >
                  <Typography variant='body1' sx={{ marginTop: '12px' }}>
                    {element.title}
                  </Typography>
                </Box>
              ) : (
                renderContent(index)
              )}
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </>
  )

  const renderDetailView = () => (
    <>
      <BackButton onClick={() => setYearIndex(undefined)} />
      {yearIndex !== undefined && renderContent(yearIndex)}
      <BackButton onClick={() => setYearIndex(undefined)} />
    </>
  )

  return (
    <Paper sx={{ margin: downSm ? '0px' : '32px', padding: downSm ? '8px' : '32px' }}>
      {typeof yearIndex === 'number' ? renderDetailView() : renderTimelineView()}
    </Paper>
  )
}

export default Biography
